if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.gallery = this.querySelector('.modal-gallery');
        this.viewer = this.querySelector('.modal-gallery__viewer');
        this.viewerSlider = this.querySelector('[id^="Slider-ModalGallery"]');
        this.zoomControls = this.querySelector('.modal-gallery__zoom-controls');

        // Don't let the media-modal backdrop auto-close fire while interacting
        // with the image, thumbnails or controls (only the dark area closes it).
        this.gallery?.addEventListener('pointerup', (event) => event.stopPropagation());

        this.querySelectorAll('[data-modal-thumb]').forEach((button) =>
          button.addEventListener('click', () => this.goToMedia(button.dataset.modalThumb))
        );

        this.querySelectorAll('[data-zoom-action]').forEach((button) =>
          button.addEventListener('click', (event) => {
            event.stopPropagation();
            this.handleZoomButton(button.dataset.zoomAction);
          })
        );

        this.viewer?.addEventListener('slideChanged', (event) => this.onSlideChanged(event));
      }

      get slides() {
        return Array.from(this.querySelectorAll('.modal-gallery__slide'));
      }

      get activeSlide() {
        return this.querySelector('.modal-gallery__slide.is-active') || this.slides[0];
      }

      show(opener) {
        super.show(opener);
        // Hide the Shopify chat widget while the fullscreen gallery is open.
        document.body.classList.add('product-media-modal-open');
        this.goToMedia(opener?.getAttribute('data-media-id'), true);
        // Re-measure the thumbnail slider now that the modal is visible, so the
        // chevrons/scroll axis are detected correctly for products with many media.
        // Run twice — once on the next frame and once after the open transition —
        // because the strip's dimensions aren't final on the first frame.
        const reinitThumbs = () => this.querySelector('.modal-gallery__thumbnails')?.resetPages?.();
        requestAnimationFrame(reinitThumbs);
        setTimeout(reinitThumbs, 250);
      }

      hide() {
        this.querySelectorAll('zoom-image').forEach((zoom) => zoom.reset?.());
        document.body.classList.remove('product-media-modal-open');
        super.hide();
      }

      goToMedia(mediaId, instant = false) {
        const slide = mediaId
          ? this.querySelector(`.modal-gallery__slide[data-media-id="${mediaId}"]`)
          : this.slides[0];
        if (!slide) return;

        this.setActiveSlide(slide);
        this.viewerSlider?.scrollTo({ left: slide.offsetLeft, behavior: instant ? 'auto' : 'smooth' });
      }

      setActiveSlide(slide) {
        // Stop any video/audio from the slide we're leaving.
        if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
        this.slides.forEach((element) => {
          if (element !== slide) {
            element.querySelector('zoom-image')?.reset?.();
            this.resetDeferredMedia(element);
          }
          element.classList.toggle('is-active', element === slide);
        });
        // Zoom only applies to images — hide the +/- controls for video/3D slides.
        this.zoomControls?.classList.toggle('hidden', !slide.querySelector('zoom-image'));
        this.setActiveThumb(slide.dataset.mediaId);
      }

      // Unload a played video when leaving its slide so it resets to the poster
      // (and stops any background playback) instead of staying in a half-played state.
      resetDeferredMedia(slide) {
        const deferred = slide.querySelector('deferred-media');
        if (!deferred || !deferred.hasAttribute('loaded')) return;
        deferred.querySelector('iframe, video')?.remove();
        deferred.removeAttribute('loaded');
      }

      setActiveThumb(mediaId) {
        this.querySelectorAll('[data-modal-thumb]').forEach((button) => {
          const current = button.dataset.modalThumb === String(mediaId);
          button.toggleAttribute('aria-current', current);
          if (current) button.closest('.thumbnail-list__item')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        });
      }

      onSlideChanged(event) {
        const current = event.detail?.currentElement;
        if (current) this.setActiveSlide(current);
      }

      handleZoomButton(action) {
        const zoom = this.activeSlide?.querySelector('zoom-image');
        if (!zoom) return;
        if (action === 'in') zoom.zoomIn();
        else zoom.zoomOut();
      }
    }
  );
}
